import { useState, useRef, useEffect } from 'react';
import { FORM_STEPS, COMPANY } from '../data';

const INPUT_STYLE = {
  width: '100%',
  background: '#1a2035',
  border: '1px solid rgba(255,255,255,0.15)',
  color: 'var(--txt)',
  padding: '13px 16px',
  borderRadius: 10,
  fontSize: 15,
  outline: 'none',
  fontFamily: 'var(--fB)',
  transition: 'border-color 0.2s',
};

/* ── Custom dropdown (replaces native <select> for dark theme visibility) ── */
function CustomSelect({ value, onChange, options, placeholder = 'Select an option…' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative', userSelect: 'none' }}>
      {/* Trigger button */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          ...INPUT_STYLE,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: value ? 'var(--txt)' : 'var(--mut)',
          borderColor: open ? 'var(--acc)' : 'rgba(255,255,255,0.15)',
          paddingRight: 40,
          minHeight: 48,
        }}
      >
        <span>{value || placeholder}</span>
        <span style={{
          position: 'absolute', right: 14, top: '50%',
          transform: `translateY(-50%) rotate(${open ? '180deg' : '0deg'})`,
          transition: 'transform 0.2s',
          color: 'var(--mut)', fontSize: 11,
        }}>▼</span>
      </div>

      {/* Options list */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
          background: '#1e2540',
          border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: 12, zIndex: 999, overflow: 'hidden',
          boxShadow: '0 16px 48px rgba(0,0,0,0.6)',
          animation: 'fadeUp 0.15s ease',
        }}>
          {options.map((opt) => (
            <div
              key={opt}
              onClick={() => { onChange(opt); setOpen(false); }}
              style={{
                padding: '12px 16px',
                fontSize: 14,
                cursor: 'pointer',
                color: value === opt ? 'var(--acc)' : 'var(--txt)',
                background: value === opt ? 'rgba(0,229,204,0.1)' : 'transparent',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                fontFamily: 'var(--fB)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { if (value !== opt) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
              onMouseLeave={e => { if (value !== opt) e.currentTarget.style.background = 'transparent'; }}
            >
              {value === opt && <span style={{ color: 'var(--acc)', fontSize: 12 }}>✓</span>}
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function LeadFormModal({ onClose }) {
  const [step, setStep]   = useState(0);
  const [done, setDone]   = useState(false);
  const [form, setForm]   = useState({
    name: '', phone: '', email: '',
    company: '', location: '', service: '', type: '',
    budget: '', message: '',
  });

  const update = (key, value) => setForm(f => ({ ...f, [key]: value }));
  const currentStep = FORM_STEPS[step];

  const isComplete = () => {
    if (step === 0) return form.name && form.phone && form.email;
    if (step === 1) return form.company && form.location && form.service && form.type;
    return true;
  };

  const isLast = step === FORM_STEPS.length - 1;
  const complete = isComplete();

  const handleNext = () => {
    if (!isLast) setStep(s => s + 1);
    else {
      setDone(true);
      const msg = encodeURIComponent(
        `Hi Neural Media Solutions!\n\n` +
        `Name: ${form.name}\nPhone: ${form.phone}\nEmail: ${form.email}\n` +
        `Company: ${form.company}\nLocation: ${form.location}\n` +
        `I am a: ${form.type}\nService: ${form.service}\n` +
        `Budget: ${form.budget || 'Not specified'}\nGoal: ${form.message || 'Not specified'}`
      );
      setTimeout(() => window.open(`${COMPANY.whatsapp}?text=${msg}`, '_blank'), 1200);
    }
  };

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(12px)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div style={{
        background: '#0e1628',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 24, padding: 44,
        maxWidth: 540, width: '100%', maxHeight: '90vh', overflowY: 'auto',
        position: 'relative',
      }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 20, background: 'none', border: 'none', color: 'var(--mut)', fontSize: 22, cursor: 'pointer' }}>✕</button>

        {done ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 60, marginBottom: 16 }}>🚀</div>
            <h2 style={{ fontFamily: 'var(--fD)', fontSize: 26, fontWeight: 900, marginBottom: 12 }}>We got your details!</h2>
            <p style={{ color: 'var(--mut)', fontSize: 15, lineHeight: 1.7, marginBottom: 24 }}>Opening WhatsApp so we can connect instantly.</p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--acc)', fontWeight: 700 }}>
              <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>↻</span> Opening WhatsApp…
            </div>
          </div>
        ) : (
          <>
            {/* Progress bar */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 32 }}>
              {FORM_STEPS.map((_, i) => (
                <div key={i} style={{ flex: 1, height: 5, borderRadius: 3, background: i === step ? 'var(--acc)' : i < step ? 'var(--acc2)' : 'var(--bdr)', transition: 'background 0.3s' }} />
              ))}
            </div>

            <h2 style={{ fontFamily: 'var(--fD)', fontWeight: 900, fontSize: 26, marginBottom: 6 }}>{currentStep.title}</h2>
            <p style={{ color: 'var(--mut)', fontSize: 14, marginBottom: 28 }}>
              Step {step + 1} of {FORM_STEPS.length} — {currentStep.sub}
              {step === 2 && <span style={{ color: 'var(--acc)', fontWeight: 600 }}> (Optional)</span>}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {currentStep.fields.map(field => (
                <div key={field.key}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--mut)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 7 }}>
                    {field.label}
                  </label>
                  {field.type === 'select' ? (
                    <CustomSelect
                      value={form[field.key]}
                      onChange={v => update(field.key, v)}
                      options={field.options}
                    />
                  ) : field.type === 'textarea' ? (
                    <textarea placeholder={field.placeholder} value={form[field.key]} onChange={e => update(field.key, e.target.value)} rows={4} style={{ ...INPUT_STYLE, resize: 'vertical' }} />
                  ) : (
                    <input type={field.type} placeholder={field.placeholder} value={form[field.key]} onChange={e => update(field.key, e.target.value)} style={INPUT_STYLE} />
                  )}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 28 }}>
              {step > 0 && (
                <button onClick={() => setStep(s => s - 1)} style={{ flex: '0 0 auto', padding: '13px 22px', background: 'none', border: '1px solid var(--bdr)', color: 'var(--txt)', borderRadius: 10, fontFamily: 'var(--fB)', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                  ← Back
                </button>
              )}
              <button
                onClick={handleNext}
                style={{
                  flex: 1, padding: '14px 22px',
                  background: complete ? 'var(--acc)' : step === 2 ? 'var(--acc)' : 'rgba(0,229,204,0.2)',
                  color:      complete || step === 2 ? '#060912' : 'rgba(0,0,0,0.35)',
                  border:     `2px solid ${complete || step === 2 ? 'var(--acc)' : 'transparent'}`,
                  borderRadius: 10, fontFamily: 'var(--fB)', fontWeight: 700, fontSize: 15,
                  cursor: complete || step === 2 ? 'pointer' : 'not-allowed',
                  boxShadow: complete || step === 2 ? '0 6px 24px rgba(0,229,204,0.25)' : 'none',
                  transition: 'all 0.2s',
                }}
              >
                {isLast ? 'Submit & Chat on WhatsApp 💬' : complete ? 'Continue →' : step === 2 ? 'Skip & Submit →' : 'Continue →'}
              </button>
            </div>
            <p style={{ textAlign: 'center', color: 'var(--mut)', fontSize: 12, marginTop: 14 }}>🔒 100% private. No spam, ever.</p>
          </>
        )}
      </div>
    </div>
  );
}
